import { useEffect, useState } from "react";
import {
  fetchAnonymousExperiments,
  AnonymousFeatureFlagStatus,
} from "../api/v1/experiments/fetchAnonymousExperiments";

export const PRE_AUTH_MAINTENANCE_FLAG_ID = "c1-pre-auth-maintenance-page";

export type PreAuthMaintenanceIcon = "error-state" | "beach-umbrella";

export interface PreAuthMaintenanceSchedule {
  enabled: boolean;
  startTime: number;
  endTime: number;
}

export interface PreAuthMaintenanceData {
  title?: string;
  subtext?: string;
  icon?: PreAuthMaintenanceIcon;
  schedule?: PreAuthMaintenanceSchedule;
}

interface PreAuthMaintenanceVariant {
  variantName: "control" | "available";
  data?: PreAuthMaintenanceData;
}

const parseVariant = (
  flag: AnonymousFeatureFlagStatus | undefined
): PreAuthMaintenanceVariant | undefined => {
  if (!flag) {
    return undefined;
  }
  try {
    return JSON.parse(flag.variant) as PreAuthMaintenanceVariant;
  } catch {
    return undefined;
  }
};

const isWithinSchedule = (schedule?: PreAuthMaintenanceSchedule): boolean => {
  if (!schedule || !schedule.enabled) {
    return true;
  }
  const now = Date.now();
  return now >= schedule.startTime && now <= schedule.endTime;
};

/**
 * Checks the `c1-pre-auth-maintenance-page` LaunchDarkly flag. Returns the
 * maintenance page's data when the flag is "available" and:
 *  1. schedule=true AND current time within the window
 *  2. OR schedule=false 
 */
export const usePreAuthMaintenance = (): PreAuthMaintenanceData | undefined => {
  const [data, setData] = useState<PreAuthMaintenanceData | undefined>(
    undefined
  );

  useEffect(() => {
    fetchAnonymousExperiments()
      .then((response) => {
        const flag = response.featureFlags.find(
          (f) => f.id === PRE_AUTH_MAINTENANCE_FLAG_ID
        );
        const variant = parseVariant(flag);
        if (variant?.variantName === "available") {
          setData(variant.data);
        } else {
          setData(undefined);
        }
      })
      .catch(() => {
        setData(undefined);
      });
  }, []);

  if (!data || !isWithinSchedule(data.schedule)) {
    return undefined;
  }
  return data;
};
